/**
 * Canonical color source for the Oak app (Encamp 2/26 Brand Guidelines).
 *
 * Guideline usage hierarchy: ~60% primary, ~30% secondary, ~10% accent.
 *
 * How to reference colors (the convention):
 * - **Mode-invariant brand color** (these hexes are the same in light/dark):
 *   import and use `encampBrandColors.x` directly. It's always defined, so it
 *   works in any context (incl. the minimal themes used in tests).
 * - **Value that should adapt per color scheme** (page/surface backgrounds,
 *   scrollbars, nav tints): use a `theme.palette` token — `canvas`,
 *   `surfaceSubtle`, `scrollbarThumb`, `nav.*` — defined per-scheme in theme.tsx.
 * - **Brand color as a component `color=` prop** (e.g. `<Button color="sapphire">`):
 *   use a promoted palette color (see `augmentColor` in theme.tsx).
 *
 * NOTE: this palette is mirrored on the theme as `theme.palette.brand`, but that
 * token is OPTIONAL in `PaletteOptions`, so `theme.palette.brand.x` can be
 * `undefined` under a theme that omits it (minimal/test themes) and is unsafe.
 * For a fixed brand color, prefer this constant — don't route through the theme.
 *
 * The MUI semantic slots (`primary`, `secondary`, `success`, …) still predate
 * these guidelines and are intentionally left at their defaults.
 */
export const encampBrandColors = {
  // Primary colors (~60%)
  /** Deep Navy Blue — dominant brand color (logo text). */
  navy: "#051E3C",
  /** Pumpkin — energy/action (logo "A"). `warning.main`; nav staff accents. */
  pumpkin: "#FF6B35",
  /** Amazon Green. */
  amazonGreen: "#3E7C4C",
  /** Blue Sapphire (teal) — login CTAs (`palette.sapphire`). The left-nav rail uses a separate muted teal (`NAV_BACKGROUND_TEAL` in theme.tsx). */
  sapphire: "#0F5F6B",

  // Secondary colors (~30%)
  white: "#FFFFFF",
  /** Light Gray — app canvas background (`palette.canvas`, light scheme). */
  lightGray: "#F3F3F3",
  /** Independence — muted blue-gray for supporting text and elements. */
  independence: "#4A5568",
  /** Rich Black — darkest navy; the guidelines' primary text color. */
  richBlack: "#041120",

  // Accent colors (~10%) — highlights and key details, used sparingly
  /** Cyan — interactive highlight; the nav selection accent (`nav.selected`). */
  cyan: "#00B4D8",
  /** Yellow-Green — accent. */
  yellowGreen: "#8DC63F",
} as const;
